import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import { Box } from '@mui/material';
import { ApButton } from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import { UiRadioCard } from '../../../../common/UiRadioCard';
import { UiRadioCardGroup } from '../../../../common/UiRadioCardGroup/UiRadioCardGroup';
import {
    WizardStepContainer,
    WizardStepHeader,
} from '../WizardStepLayout';
import useGovernancePolicyCreateEnforcementStepViewModel from './GovernancePolicyCreateEnforcementStepViewModel';

interface GovernancePolicyCreateEnforcementStepProps {
    type?: 'add' | 'edit';
    isDrawerMode?: boolean;
    onDone?: () => void;
}

export const GovernancePolicyCreateEnforcementStep: React.FC<GovernancePolicyCreateEnforcementStepProps> = ({
    type = 'add', isDrawerMode = false, onDone,
}) => {
    const { formatMessage: translate } = useIntl();
    const {
        policyStatus,
        statusModeCards,
        handlePolicyStatusChange,
    } = useGovernancePolicyCreateEnforcementStepViewModel();

    const radioCards = (
        <UiRadioCardGroup
            selectedMode={policyStatus}
            onModeChange={handlePolicyStatusChange}
            dataCyPrefix="governance-status-mode"
        >
            {statusModeCards.map(card => (
                <UiRadioCard
                    key={card.id}
                    id={card.id}
                    value={card.value}
                    title={card.title}
                    description={card.description}
                    icon={card.icon}
                    isSelected={policyStatus === card.value}
                    onCardClick={handlePolicyStatusChange}
                    dataCyPrefix="governance-status-mode"
                />
            ))}
        </UiRadioCardGroup>
    );

    if (isDrawerMode) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                }}
                data-cy="governance-policy-create-enforcement-step"
            >
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: SpacingToken.L,
                }}>
                    <UiStack
                        direction="column"
                        gap={SpacingToken.L}
                        p={SpacingToken.XL}
                        data-cy="governance-policy-enforcement-mode-container"
                    >
                        {radioCards}
                    </UiStack>
                </Box>
                <Box
                    sx={{
                        borderTop: '1px solid var(--color-border-de-emp)',
                        background: 'var(--color-background)',
                        padding: '12px',
                        flexShrink: 0,
                    }}
                >
                    <UiStack
                        direction="row"
                        justify="end"
                        gap={SpacingToken.M}
                        p={SpacingToken.S}
                    >
                        <ApButton
                            variant="primary"
                            onClick={onDone}
                            label={translate({ id: 'CLIENT_DONE' })}
                            data-cy="governance-policy-enforcement-done"
                        />
                    </UiStack>
                </Box>
            </Box>
        );
    }

    return (
        <WizardStepContainer
            type={type}
            data-cy="governance-policy-create-enforcement-step">
            <UiStack
                direction="column"
                gap={SpacingToken.L}
                data-cy="governance-policy-enforcement-mode-container"
            >
                <WizardStepHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT_MODE' })}
                    description={translate({ id: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT_MODE_DESCRIPTION' })}
                />
                <Box sx={{ width: '700px' }}>
                    {radioCards}
                </Box>
            </UiStack>
        </WizardStepContainer>
    );
};
